import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    localContent: string;
    serverContent: string;
    competingContent?: string;
    disabled: boolean;
    reviewRequest?: number;
    onResolve: (keepLocal: boolean) => Promise<'resolved' | 'updated' | 'failed'>;
}

const SummaryDraftConflict: React.FC<Props> = ({ localContent, serverContent, competingContent, disabled, reviewRequest, onResolve }) => {
    const { t } = useTranslation();
    const details = useRef<HTMLDetailsElement>(null);
    const trigger = useRef<HTMLElement>(null);
    const resolving = useRef(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<'updated' | 'failed' | null>(null);
    useEffect(() => {
        if (!reviewRequest || !details.current) return;
        details.current.open = true;
        trigger.current?.focus({ preventScroll: true });
        details.current.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }, [reviewRequest]);

    const resolve = async (keepLocal: boolean) => {
        if (disabled || resolving.current) return;
        resolving.current = true;
        setBusy(true);
        setMessage(null);
        try {
            const result = await onResolve(keepLocal);
            if (result !== 'resolved') setMessage(result);
        } catch {
            setMessage('failed');
        } finally {
            resolving.current = false;
            setBusy(false);
        }
    };

    return (
        <details ref={details} className="summary-draft-conflict" onKeyDown={event => {
            if (event.key === 'Escape' && details.current?.open) {
                event.preventDefault();
                event.stopPropagation();
                details.current.open = false;
                trigger.current?.focus();
            }
        }}>
            <summary ref={trigger} className="summary-draft-conflict-trigger">
                <span className="summary-draft-conflict-label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 11a9 9 0 1 1 2.7 7M3 4v7h7M12 7v5l3 2" /></svg>
                    <span>{t('summaryEditor.draft.conflict')}</span>
                </span>
                <span className="summary-draft-conflict-toggle">
                    <span className="summary-draft-conflict-show">{t('summaryEditor.draft.review')}</span>
                    <span className="summary-draft-conflict-hide">{t('summaryEditor.draft.close')}</span>
                    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="m6 8 4 4 4-4" /></svg>
                </span>
            </summary>
            <div className="summary-draft-comparison">
                <p className="summary-draft-comparison-hint">{t('summaryEditor.draft.compareHint')}</p>
                <div className="summary-draft-versions">
                    {[
                        { label: 'localVersion', content: localContent },
                        { label: 'serverVersion', content: serverContent },
                        ...(competingContent !== undefined ? [{ label: 'otherTab', content: competingContent }] : []),
                    ].map(version => <section className="summary-draft-version" key={version.label} aria-label={t(`summaryEditor.draft.${version.label}`)}>
                        <h3>{t(`summaryEditor.draft.${version.label}`)}</h3>
                        <pre tabIndex={0}>{version.content || t('summaryEditor.draft.emptyVersion')}</pre>
                    </section>)}
                </div>
                <div className="summary-draft-comparison-actions" aria-busy={busy}>
                    <button className="summary-draft-action-primary" type="button" disabled={disabled || busy} onClick={() => { void resolve(true); }}>{t('summaryEditor.draft.keepLocal')}</button>
                    <button type="button" disabled={disabled || busy} onClick={() => { void resolve(false); }}>{t('summaryEditor.draft.useServer')}</button>
                </div>
                {(busy || message) && <p className="summary-draft-comparison-feedback" role="status">{t(`summaryEditor.draft.${busy ? 'checking' : message}`)}</p>}
            </div>
        </details>
    );
};

export default SummaryDraftConflict;
